import React, {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ForwardedRef,
  forwardRef,
  ReactElement,
  ReactNode,
  RefAttributes,
} from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { UIElementProps } from 'types/prop-types';

/** Base legada: usada quando o botão não recebe `variant` (telas ainda não migradas). */
type LegacyButtonProps = UIElementProps & {
  variant?: undefined;
  children: React.ReactNode;
  onClick?: (e: any) => void;
  href?: string;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  tag?: 'button' | 'a' | 'input';
  style?: React.CSSProperties;
  arialabel?: string;
  /**
   * @deprecated Cores da identidade antiga. Em telas novas use `variant`.
   */
  color?:
    | 'clear'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark'
    | 'outline'
    | 'outlinePurple'
    | 'featured'
    | 'pink';
  [key: string]: any;
};

export type ButtonVariant = 'principal' | 'solido' | 'solido-destaque' | 'secundario' | 'texto';

/** `sm`: o botão pequeno do design (32px), para rodapés de painel com várias ações. */
export type ButtonSize = 'md' | 'sm';

type VariantOwnProps = {
  /** Estilo da nova identidade. Com ele, nada da base legada é aplicado. */
  variant: ButtonVariant;
  /** Tamanho. Padrão `md`. */
  size?: ButtonSize;
  children: ReactNode;
  className?: string;
  arialabel?: string;
};

type VariantAsButtonProps = VariantOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    to?: undefined;
    href?: undefined;
  };

/** Rota interna. Link não tem estado desabilitado real, por isso não aceita `disabled`. */
type VariantAsRouterLinkProps = VariantOwnProps &
  Omit<LinkProps, 'className' | 'children'> & {
    href?: undefined;
    disabled?: never;
  };

/** Link externo. Link não tem estado desabilitado real, por isso não aceita `disabled`. */
type VariantAsAnchorProps = VariantOwnProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'> & {
    href: string;
    to?: undefined;
    disabled?: never;
  };

type VariantProps = VariantAsButtonProps | VariantAsRouterLinkProps | VariantAsAnchorProps;

export type ButtonProps = LegacyButtonProps | VariantProps;

/**
 * O `forwardRef` apaga as props de tipos com índice `[key: string]: any` (o `Omit` do
 * `PropsWithoutRef` vira um objeto só de índice), e o tipo deixaria de barrar
 * `disabled` em link. Esta assinatura declara a união como ela é. O `ref` chega só
 * ao `<button>`.
 */
type ButtonComponent = (
  props:
    | (LegacyButtonProps & RefAttributes<HTMLButtonElement>)
    | (VariantAsButtonProps & RefAttributes<HTMLButtonElement>)
    | VariantAsRouterLinkProps
    | VariantAsAnchorProps,
) => ReactElement | null;

const buttonColor: any = {
  clear: '',
  primary: 'bg-purple text-white',
  secondary: 'bg-blue-light text-blue-dark',
  danger: 'bg-red-600 text-white ring-2 ring-white',
  success: 'bg-green-600 text-white ring-2 ring-white',
  outline: 'bg-transparent text-yellow ring-2 ring-yellow',
  outlinePurple: 'bg-transparent text-white ring-2 ring-purple',
  featured: 'bg-yellow text-blue-dark',
  pink: 'bg-[#E46382] text-white',
};

const VARIANT_BASE_CLASSES =
  'inline-flex items-center justify-center rounded-jj-md font-text font-bold uppercase tracking-[.04em] transition-colors duration-jj-fast ease-jj-standard focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue-dark';

const SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'px-6 py-3 text-sm',
  sm: 'px-[18px] py-2 text-[11.5px] leading-4',
};

// O `texto` não tem fundo: o recuo lateral curto do design alinha a palavra ao botão vizinho.
const TEXT_SIZE_CLASSES: Record<ButtonSize, string> = {
  md: 'px-3 py-3 text-sm',
  sm: 'px-2 py-2 text-[11.5px] leading-4',
};

const VARIANT_CLASSES: Record<ButtonVariant, { color: string; hover: string }> = {
  principal: { color: 'bg-brand-yellow text-brand-blue-dark', hover: 'hover:bg-button-primary-hover' },
  solido: { color: 'bg-brand-blue text-brand-white', hover: 'hover:bg-brand-blue-dark' },
  // Ação principal dos painéis de programa da Home (design v2): azul com texto amarelo, num azul um
  // pouco mais escuro que o da marca para passar no AA (5,22:1); o hover escurece (--brand-blue-dark).
  'solido-destaque': { color: 'bg-button-accent text-brand-yellow', hover: 'hover:bg-brand-blue-dark' },
  // A task não define hover para o secundário: só escurece ao pressionar.
  secundario: { color: 'bg-brand-yellow-light text-brand-blue-dark', hover: '' },
  // Ação secundária sem fundo ("Acompanhar realizações"): só o texto, sublinhado no hover.
  texto: { color: 'bg-transparent text-brand-blue', hover: 'hover:underline' },
};

// Como link (`to`/`href`), o `rsuite.min.css` pinta todo `a:hover`/`a:focus` de #0a5dc2 com
// sublinhado, e o seletor dele vence a cor do texto da variante: no sólido o texto sumia no
// azul. A trava repete a cor da variante nesses estados. Só vale para link, que não tem
// estado desabilitado — o botão desabilitado continua sem nenhuma classe de hover.
const LINK_TEXT_LOCK: Record<ButtonVariant, string> = {
  principal: 'hover:text-brand-blue-dark focus:text-brand-blue-dark',
  solido: 'hover:text-brand-white focus:text-brand-white',
  'solido-destaque': 'hover:text-brand-yellow focus:text-brand-yellow',
  secundario: 'hover:text-brand-blue-dark focus:text-brand-blue-dark',
  texto: 'hover:text-brand-blue focus:text-brand-blue',
};
// O `texto` se distingue pelo sublinhado no hover; os demais nunca sublinham.
const LINK_DECORATION_LOCK: Record<ButtonVariant, string> = {
  principal: 'hover:no-underline focus:no-underline',
  solido: 'hover:no-underline focus:no-underline',
  'solido-destaque': 'hover:no-underline focus:no-underline',
  secundario: 'hover:no-underline focus:no-underline',
  texto: 'hover:underline focus:no-underline',
};

const PRESSED_CLASS = 'active:brightness-90';
const DISABLED_CLASSES = 'cursor-not-allowed opacity-[.45]';

function variantClassName(variant: ButtonVariant, size: ButtonSize, disabled: boolean, className?: string) {
  const { color, hover } = VARIANT_CLASSES[variant];
  const interaction = disabled ? DISABLED_CLASSES : `${hover} ${PRESSED_CLASS}`;

  const sizeClasses = variant === 'texto' ? TEXT_SIZE_CLASSES[size] : SIZE_CLASSES[size];

  return [VARIANT_BASE_CLASSES, sizeClasses, color, interaction, className].filter(Boolean).join(' ');
}

function linkClassName(variant: ButtonVariant, size: ButtonSize, className?: string) {
  return [variantClassName(variant, size, false), LINK_TEXT_LOCK[variant], LINK_DECORATION_LOCK[variant], className]
    .filter(Boolean)
    .join(' ');
}

/** Escolhe o elemento pela prop: `to` → `Link`, `href` → `<a>`, senão `<button>`. */
function renderVariant(props: VariantProps, ref: ForwardedRef<HTMLButtonElement>) {
  const { variant, size = 'md', className, children, arialabel, 'aria-label': ariaLabel, ...rest } = props;
  const label = arialabel ?? ariaLabel;

  if (rest.to !== undefined) {
    return (
      <Link {...rest} className={linkClassName(variant, size, className)} aria-label={label}>
        {children}
      </Link>
    );
  }

  if (rest.href !== undefined) {
    return (
      <a {...rest} className={linkClassName(variant, size, className)} aria-label={label}>
        {children}
      </a>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={variantClassName(variant, size, Boolean(rest.disabled), className)}
      aria-label={label}
    >
      {children}
    </button>
  );
}

/**
 * Botão da aplicação.
 *
 * - Com `variant` (`principal`, `solido`, `solido-destaque`, `secundario`, `texto`): visual da nova identidade.
 *   `to` renderiza `Link` (rota interna) e `href` renderiza `<a>` (link externo).
 *   Como `<button>`, o `type` padrão é `button`; passe `type="submit"` para enviar formulário.
 * - Sem `variant`: visual legado, controlado por `color` (deprecated).
 *
 * Contraste (TEST-003, aprovado pelo Caio em 2026-09-30): o texto do `principal` e do
 * `secundario` é `brand-blue-dark` (8,13:1 sobre o amarelo, 8,18:1 sobre o creme); com
 * `brand-blue` ficava em 4,4:1. O hover do `solido` é `brand-blue-dark` (8,94:1 com o
 * branco), no lugar do `button-solid-hover`, que dava 4,40:1.
 *
 * Foco sobre fundo azul: o anel padrão (`brand-blue-dark`) fica com 1,83:1 contra
 * o azul. Nessas telas, passe `focus-visible:outline-brand-white` (ou o amarelo)
 * no `className`.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  if (props.variant !== undefined) return renderVariant(props, ref);

  const { children, className, color, arialabel, 'aria-label': ariaLabel, ...rest } = props;

  return (
    <button
      ref={ref}
      {...rest}
      className={`${
        buttonColor[color || 'primary']
      } hover:ring hover:ring-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow rounded-md shadow-xl font-bold px-4 ${className || ''}`}
      aria-label={arialabel ?? ariaLabel}
    >
      <div className="p-1 leading-tight">{children}</div>
    </button>
  );
}) as ButtonComponent;

export default Button;
